import { useRef, useState } from 'react'
import { usePlayer } from '@/audio/store'
import { useAutoplay } from '@/audio/autoplay'
import { DISMISS_DISTANCE, SWIPE_DISTANCE, type GestureOptions } from './gestures/gesture'
import { useSurfaceGesture } from './gestures/useSurfaceGesture'

interface Offset { x: number; y: number; label: string }

/** Shared by the native event adapter and transport regression tests. */
export function resolvePlayerGesture(target: Element, mouse: boolean, onClose: () => void, setOffset: (offset: Offset) => void, lastSkip: { current: number }): GestureOptions | null {
  if (mouse || target.closest('button, a, input, [role="slider"], [data-gesture-row], [data-gesture-ignore], [data-swipe-surface]')) return null
  const root = target.closest('[data-player-view]')
  const scroller = root?.querySelector('[data-player-scroll]')
  const cover = !!target.closest('[data-player-cover]')
  const canDismiss = cover || !!target.closest('[data-player-header]') || (scroller?.scrollTop ?? 0) <= 0
  if (!cover && !canDismiss) return null
  const canNext = () => {
    const state = usePlayer.getState()
    return state.index < state.queue.length - 1 || state.repeat === 'all' || (state.repeat === 'off' && useAutoplay.getState().enabled)
  }
  return {
    directions: [...(cover ? ['left' as const, 'right' as const] : []), ...(canDismiss ? ['down' as const] : [])],
    // Down and horizontal gestures have independent travel thresholds.
    threshold: SWIPE_DISTANCE,
    onActivate() {},
    onMove: (mode, _point, distance) => {
      if (mode === 'down') setOffset({ x: 0, y: Math.min(400, distance), label: distance >= DISMISS_DISTANCE ? 'Release to close' : 'Close player' })
      else setOffset({ x: (mode === 'left' ? -1 : 1) * Math.min(112, distance), y: 0,
        label: mode === 'left' ? usePlayer.getState().awaitingContinuation ? 'Preparing next song…' : canNext() ? 'Next song' : 'End of queue'
          : usePlayer.getState().currentTime > 3 ? 'Restart song' : 'Previous song' })
    },
    onCommit: (mode) => {
      if (mode === 'down') { onClose(); return }
      if (performance.now() - lastSkip.current < 350) return
      const state = usePlayer.getState()
      if (mode === 'left' && (!canNext() || state.awaitingContinuation)) return
      lastSkip.current = performance.now()
      if (mode === 'left') state.next(false)
      else state.previous()
    },
    onReset: () => setOffset({ x: 0, y: 0, label: '' }),
    // The generic controller permits per-direction distances, including downward dismissal.
    thresholds: { down: DISMISS_DISTANCE },
  }
}

export function usePlayerGestures(open: boolean, onClose: () => void) {
  const identity = usePlayer((state) => `${state.session}:${state.index}:${state.intentVersion}:${state.queue.map((item) => item.uid).join(',')}`)
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0, label: '' })
  const lastSkip = useRef(-Infinity)
  const { ref } = useSurfaceGesture((target, _origin, mouse) => resolvePlayerGesture(target, mouse, onClose, setOffset, lastSkip), identity, open)
  return { ref, offset }
}
